import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useDispatch } from "react-redux";

import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import Switch from "../../../../../Common/Switch";
import SimpleTable from "../../../../../Common/Table/SimpleTable";
import { showToast } from "../../../../../../store/actions";
import Footer from "./Footer";

const useStyles = makeStyles()((theme) => ({
  formContainer: {
    display: "grid",
    width: "100%",
    textAlign: "left",
    minHeight: "150px",
    "& .MuiPaper-root": {
      marginTop: "20px",
      alignItems: "center",
      borderRadius: 10,
    },
    "& .MuiAlert-icon": {
      padding: 0,
      backgroundColor: "#67A7D5",
      borderTopLeftRadius: "10px",
      borderBottomLeftRadius: "9px",
      height: "53px",
      alignItems: "center",
      "& .MuiSvgIcon-root": {
        height: "100%",
        color: "#FFFFFF",
      },
    },
    "& .MuiAlert-message": {
      padding: "5px",
      color: "#A2A2A2",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      top: 0,
    },
    "& .MuiFab-root": {
      marginTop: "16px",
    },
    "& .MuiIconButton-sizeLarge": {
      padding: 0,
      marginTop: 10,
      justifyContent: "left",
      width: "fit-content",
    },
  },
  container: {
    padding: "15px 0 0 0",
    backgroundColor: "#FFFFFF",
  },
}));

// default Row for List of Values table
const defaultData = () => [
  {
    value: "",
    editable: true,
    textWidth: 130,
  },
  {
    value: "",
    editable: true,
    textWidth: 130,
  },
  {
    deleteIcon: <DeleteOutlineIcon color={"error"} />,
  },
];

const valueToLov = (values) =>
  values.map(({ source, target }) => [
    {
      value: source,
      editable: true,
      textWidth: 130,
    },
    {
      value: target,
      editable: true,
      textWidth: 130,
    },
    {
      deleteIcon: <DeleteOutlineIcon color={"error"} />,
    },
  ]);

const columns = [
  {
    name: "Source",
    width: 130,
    align: "left",
  },
  {
    name: "Target",
    width: 130,
    align: "left",
  },
  {
    name: "",
    width: 30,
    align: "left",
  },
];

const ListofValue = ({ addField, fields, currentField, onClose }, ref) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  // State Data for LoV Tab
  const [tableData, setTableData] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [lovDescription, setLovDescription] = useState("");
  const [lovDefaultValue, setLovDefaultValue] = useState("");
  const [useSource, setUseSource] = useState(true);

  const enableLOVSave = () =>
    !(
      !enabled ||
      (enabled &&
        tableData.some((itemArray) => itemArray[0].value.trim()) &&
        (useSource || (!useSource && lovDefaultValue.trim())))
    );

  const handleTextChange = useCallback((event, key, index) => {
    let value = event.target.value;

    setTableData((prev) => {
      prev[key][index].value = value;
      return [...prev];
    });
  }, []);

  const handleDeleteClick = useCallback(
    (key) => {
      if (tableData.length === 1) return setTableData([defaultData()]);

      setTableData((prev) => {
        prev.splice(key, 1);
        return [...prev];
      });
    },
    [tableData]
  );

  useEffect(() => {
    setTableData([]);
    if (currentField) {
      fields.forEach((item) => {
        if (item.name.toLowerCase() === currentField.toLowerCase()) {
          const lov = item.lov || {
            enabled: false,
            useSource: true,
            defaultValue: "",
            description: "",
            values: [],
          };

          setEnabled(lov.enabled);
          setLovDescription(lov.description);
          setUseSource(lov.useSource);
          setLovDefaultValue(lov.defaultValue);

          if (lov.values?.length) setTableData(valueToLov(lov.values));
          else setTableData([defaultData()]);
        }
      });
    }
  }, [currentField, fields]);

  const addNewRow = () => setTableData((prev) => [...prev, defaultData()]);

  const handleEnabled = () => {
    if (enabled) {
      setEnabled(false);
      setTableData([defaultData()]);
      setLovDescription("");
      setLovDefaultValue("");
      setUseSource(true);
    } else setEnabled(true);
  };

  useImperativeHandle(ref, () => ({ handleSaveDetails }));

  const handleSaveDetails = (saveBy) => {
    const tField = fields.find((field) => field.name === currentField);

    addField({
      ...tField,
      lov: {
        enabled,
        useSource,
        defaultValue: lovDefaultValue,
        description: lovDescription,
        values: tableData
          .filter((itemArray) => !!itemArray[0].value.trim())
          .map((itemArray) => ({
            source: itemArray[0].value.trim(),
            target: itemArray[1].value.trim(),
          })),
      },
    });

    if (saveBy !== "auto") {
      dispatch(
        showToast({
          message: `Attribute ${currentField ? "saved" : "added"} successfully.`,
          type: "success",
        })
      );
      onClose();
    }
  };

  return (
    <>
      <Box sx={{ width: "100%" }} className={classes.container}>
        <Box sx={{ width: "100%" }} component="form" className={classes.formContainer}>
          <FormControl className={classes.FormControl} sx={{ marginTop: "10px" }} component="fieldset">
            <FormGroup aria-label="position" row>
              <FormControlLabel
                value={enabled}
                control={<Switch />}
                label="Enable LoV"
                labelPlacement="start"
                sx={{ margin: "0" }}
                onChange={handleEnabled}
                checked={enabled}
              />
            </FormGroup>
          </FormControl>
          <SimpleTable
            configData={{
              columns: columns,
              tableData: tableData,
              setTableData: setTableData,
              textMaxLength: 50,
              tableMaxHeight: "310px",
              handleTextChange: handleTextChange,
              handleDeleteClick: handleDeleteClick,
            }}
          />
          <Fab
            sx={{
              width: "30px",
              height: "30px",
              minHeight: "0px",
            }}
            size="small"
            color="info"
            aria-label="add"
            onClick={addNewRow}
          >
            <AddIcon />
          </Fab>

          <Box sx={{ mt: 2 }}>When None match</Box>

          <Grid container spacing={1}>
            <Grid style={{ display: "flex", alignItems: "center" }} size={6}>
              <FormControlLabel
                control={<Checkbox checked={useSource} onChange={() => setUseSource((prev) => !prev)} />}
                label="Use source value"
              />
            </Grid>
            <Grid size={6}>
              <TextField
                disabled={useSource}
                required
                value={lovDefaultValue}
                inputProps={{ maxLength: 50 }}
                onChange={(e) => setLovDefaultValue(e.target.value)}
                label="Default Value"
                variant="outlined"
                sx={{ my: 2 }}
              />
            </Grid>
          </Grid>

          <TextField
            value={lovDescription}
            inputProps={{ maxLength: 64 }}
            onChange={(e) => setLovDescription(e.target.value)}
            fullWidth
            label="Description"
            variant="outlined"
          />
        </Box>
      </Box>
      <Footer disabled={enableLOVSave()} handleSaveDetails={handleSaveDetails} onClose={onClose} />
    </>
  );
};

export default forwardRef(ListofValue);
